/**
 * What the portal shows for an address, before anything else runs. Four pages need no tenant and no session, so they
 * open on any host (a tenant's address, `staging.teslyn.com.au`, `*.pages.dev`, localhost, or an address no tenant
 * uses): the Teslyn Field return page (R19), the privacy policy and terms of use (Google's OAuth app links to them),
 * a pilot hazard pack's landing page (`/hazard-pack/<token>`, docs/specs/cloud/hazard-packs.md: the link is the
 * key). `/mobile` (the address of Teslyn Mobile's QR code, docs/specs/mobile/mobile-distribution.md §3) is the
 * portal's "Teslyn Mobile" page, behind the sign-in (decision 912).
 * Everything else resolves the tenant from the hostname first (brand/white-label.md), then shows the portal.
 *
 * Before all of that, a forwarding address (`cloud.teslyn.com.au`, decision 681) sends the browser on, and on the
 * front door's address (`cloud.teslyn.au`, decision 699) the portal is Teslyn's own sign-in, which sends each person
 * on to their company's address (docs/specs/cloud/front-door.md).
 */
import type { ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { App } from './App.tsx';
import { FieldSignedIn, isFieldReturnPage } from './auth/FieldSignedIn.tsx';
import { MobileSignedIn, isMobileReturnPage } from './auth/MobileSignedIn.tsx';
import type { AuthOutcome } from './auth/authReturn.ts';
import type { WebConfig } from './config.ts';
import { legalDocFor, type LegalDoc } from './legal/legal.ts';
import { LegalPage } from './legal/LegalPage.tsx';
import { HazardPackPage } from './hazard-packs/HazardPackPage.tsx';
import { isMobilePage, mobilePageSearch } from './mobile-app/GetMobile.tsx';
import { hazardPackTokenOfPath } from '@teslyn/domain';
import { forwardTarget, isFrontDoor } from './frontdoor/address.ts';
import { FrontDoor, Opening, TESLYN_FRONT_DOOR } from './frontdoor/FrontDoor.tsx';
import { applyThemeAndTitle, type TenantResult } from './tenant/tenant.ts';

export type Route =
  | { readonly kind: 'field-return' }
  | { readonly kind: 'mobile-return' }
  | { readonly kind: 'legal'; readonly doc: LegalDoc }
  | { readonly kind: 'hazard-pack'; readonly token: string }
  | { readonly kind: 'mobile' }
  | { readonly kind: 'portal' };

/** The route of a path. Only `portal` needs the tenant (and so the hostname). */
export function routeFor(pathname: string): Route {
  if (isFieldReturnPage(pathname)) return { kind: 'field-return' };
  if (isMobileReturnPage(pathname)) return { kind: 'mobile-return' };
  const doc = legalDocFor(pathname);
  if (doc) return { kind: 'legal', doc };
  const token = hazardPackTokenOfPath(pathname);
  if (token) return { kind: 'hazard-pack', token };
  if (isMobilePage(pathname)) return { kind: 'mobile' };
  return { kind: 'portal' };
}

export interface BootDeps {
  /** The Supabase client; called only for the portal. */
  readonly supabase: () => SupabaseClient;
  /** Resolve the tenant and paint its theme; called only for the portal. */
  readonly bootstrapTenant: (
    sb: SupabaseClient,
    loc: Pick<Location, 'hostname' | 'search'>,
    cfg: WebConfig,
  ) => Promise<TenantResult>;
  /**
   * Finish a sign-in the address brings back (auth/authReturn.ts, decision 801): the words for the sign-in page when
   * it did not sign the person in, an email link waiting for its button, or null. Called only for the portal; none
   * in tests that do not need it.
   */
  readonly finishAuthReturn?: (sb: SupabaseClient) => Promise<AuthOutcome>;
  /** Go to another address, replacing this one (the forward of decision 681); `location.replace` by default. */
  readonly navigate?: (url: string) => void;
}

/** The page for this address. The public routes never create a Supabase client or look up a tenant. */
export async function bootPage(
  loc: Pick<Location, 'hostname' | 'pathname' | 'search'> & { readonly hash?: string },
  config: WebConfig,
  deps: BootDeps,
): Promise<ReactElement> {
  // a forwarding address sends the browser on before anything else runs, whatever the page
  const forward = forwardTarget(loc, config.forwardHosts);
  if (forward) {
    (deps.navigate ?? ((url: string) => window.location.replace(url)))(forward);
    return <Opening what={new URL(forward).hostname} />;
  }
  const route = routeFor(loc.pathname);
  if (route.kind === 'field-return') return <FieldSignedIn />;
  if (route.kind === 'mobile-return') return <MobileSignedIn />;
  if (route.kind === 'legal') return <LegalPage doc={route.doc} search={loc.search} />;
  if (route.kind === 'hazard-pack')
    return <HazardPackPage token={route.token} supabaseUrl={config.supabaseUrl} />;
  if (route.kind === 'mobile') {
    // the QR code's address: the "Teslyn Mobile" page, after the sign-in (which keeps the address it started on)
    const search = mobilePageSearch(loc.search);
    window.history.replaceState(null, '', `/${search}`);
    loc = { hostname: loc.hostname, pathname: '/', search };
  }
  const sb = deps.supabase();
  if (isFrontDoor(loc, config)) {
    applyThemeAndTitle(TESLYN_FRONT_DOOR); // Teslyn's own look, before the first paint
    const notice = deps.finishAuthReturn ? await deps.finishAuthReturn(sb).catch(() => null) : null;
    return (
      <FrontDoor
        sb={sb}
        config={config}
        authNotice={notice}
        {...(deps.navigate ? { navigate: deps.navigate } : {})}
      />
    );
  }
  const [tenant, authNotice] = await Promise.all([
    deps.bootstrapTenant(sb, loc, config),
    deps.finishAuthReturn ? deps.finishAuthReturn(sb).catch(() => null) : Promise.resolve(null),
  ]);
  return <App sb={sb} tenant={tenant} config={config} authNotice={authNotice} />;
}
